import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServicoTenant } from './core/tenant/servico-tenant';

export interface Empregador {
  id?: string;
  razaoSocial: string;
  nomeFantasia?: string;
  documento: string;
  inscricaoEstadual?: string;
  endereco?: string;
  cidade?: string;
  uf?: string;
  slugTenant?: string;
  dominio?: string;
  modoApiLeitor?: 'LOCAL' | 'EXTERNO';
  urlApiLocalLeitor?: string;
  ipApiLeitor?: string;
  portaApiLeitor?: number;
  logoBase64?: string;
  logoMimeType?: string;
  horarioPrimeiraBatida?: string;
  horarioSegundaBatida?: string;
  horarioTerceiraBatida?: string;
  horarioQuartaBatida?: string;
  toleranciaMinutos?: number;
  validarJanelaPonto?: boolean;
  ativo: boolean;
}

export interface Funcionario {
  id?: string;
  empregador?: Empregador;
  empregadorId?: string;
  nome: string;
  matricula: string;
  cpf: string;
  pisPasep?: string;
  cargo?: string;
  departamento?: string;
  departamentoId?: string;
  departamentoNome?: string;
  dataAdmissao?: string;
  jornadaId?: string;
  jornadaNome?: string;
  imagemFacialBase64?: string;
  imagemFacialMimeType?: string;
  descritorFacial?: number[];
  faceCadastrada?: boolean;
  biometriaConsentidaEm?: string;
  origemCapturaBiometrica?: string;
  versaoDescritorFacial?: string;
  faces?: FuncionarioFace[];
  senhaPortal?: string;
  ativo: boolean;
  podeSolicitarOficio?: boolean;
}

export interface FuncionarioFace {
  id: string;
  principal: boolean;
  ativo: boolean;
  imagemBase64?: string;
  imagemMimeType?: string;
  versaoDescritorFacial?: string;
  criadoEm: string;
}

export interface RegistroPonto {
  id: string;
  funcionario: Funcionario;
  dataHora: string;
  metodo: string;
  nsr: number;
  numeroBatida?: number;
  offlineId?: string;
}

export interface RegistroPontoAlteracao {
  id: string;
  numeroBatida: number;
  operacao: string;
  nsrAnterior?: number;
  nsrNovo?: number;
  dataHoraAnterior?: string;
  dataHoraNova?: string;
  metodoAnterior?: string;
  metodoNovo?: string;
  tinhaCapturaFacial: boolean;
  usuarioNome?: string;
  origem?: string;
  criadoEm: string;
}

export interface ItemAcertoPonto {
  eventoId: string;
  funcionarioId?: string;
  matricula: string;
  nome: string;
  data: string;
  criadoEm: string;
  motivoLog: string;
  numeroBatidaSugerida?: number;
  situacao: string;
  motivoAtual: string;
}

export interface PreviewAcertoPonto {
  candidatos: ItemAcertoPonto[];
  ignorados: ItemAcertoPonto[];
}

export interface ResultadoAcertoPonto {
  gravados: number;
  jaAplicados: number;
  recusados: number;
  preview: PreviewAcertoPonto;
}

export interface PendenciaPontoLinha {
  chave: string;
  dataIso: string;
  data: string;
  diaSemana: string;
  matricula: string;
  nome: string;
  batidas: string[];
  batidasEsperadas: number;
  batidasFaltantes: string[];
}

export interface RegistroPontoOfflineEnvio {
  imagemFacialBase64: string;
  imagemFacialMimeType: string;
  descritorFacial: number[];
  offlineId: string;
  dataHora?: string;
}

export interface SincronizacaoOfflineItemResponse {
  offlineId: string;
  sincronizado: boolean;
  status: string;
  mensagem?: string;
  registro?: RegistroPonto;
}

export interface SincronizacaoOfflineResponse {
  total: number;
  sincronizados: number;
  falhas: number;
  itens: SincronizacaoOfflineItemResponse[];
}

export interface FuncionarioCacheBiometrico {
  faceId?: string;
  funcionarioId: string;
  matricula: string;
  nome: string;
  descritorFacial: number[];
  versaoDescritorFacial?: string;
  biometriaConsentidaEm?: string;
}

export interface CacheBiometricoResponse {
  empregadorId: string;
  dispositivoId: string;
  geradoEm: string;
  expiraEm: string;
  validadeMinutos: number;
  funcionarios: FuncionarioCacheBiometrico[];
}

export type PerfilUsuario = 'ADMIN_GLOBAL' | 'ADMIN_EMPREGADOR' | 'SERVIDOR';

export interface Usuario {
  id?: string;
  empregador?: Empregador;
  empregadorId?: string;
  nome: string;
  email: string;
  senha?: string;
  perfil: PerfilUsuario;
  departamentoId?: string;
  departamentoNome?: string;
  departamentoIds?: string[];
  departamentos?: Departamento[];
  funcionarioId?: string;
  senhaProvisoria?: boolean;
  ativo: boolean;
}

export interface ResultadoGeracaoAcessos {
  criados: number;
  jaExistiam: number;
  ignorados: number;
}

export interface BatidaServidor {
  nsr: number;
  hora: string;
  numeroBatida?: number;
  metodo?: string;
}

export interface DiaBatidasServidor {
  data: string;
  rotulo: string;
  batidas: BatidaServidor[];
}

export interface PortalServidorBatidas {
  mes: string;
  rotuloMes: string;
  nome: string;
  cpf: string;
  jornada?: string;
  podeOficios?: boolean;
  dias: DiaBatidasServidor[];
}

export type TipoDocumentoOficial = 'OFICIO' | 'PORTARIA' | 'DECRETO';

export interface DocumentoOficial {
  id: string;
  tipo: TipoDocumentoOficial;
  numero: number;
  ano: number;
  dataSolicitacao: string;
  solicitante: string;
  departamento: string;
  finalidade?: string;
  origem?: 'CADASTRO' | 'SOLICITACAO' | 'RESERVA';
}

export type StatusSolicitacao = 'PENDENTE' | 'DEFERIDA' | 'INDEFERIDA';

export interface SolicitacaoAjustePonto {
  id: string;
  funcionarioId?: string;
  nome: string;
  matricula: string;
  data: string;
  hora: string;
  numeroBatida: number;
  motivo: string;
  status: StatusSolicitacao;
  parecer?: string;
  decididoPor?: string;
  criadoEm: string;
  decididoEm?: string;
}

export interface SolicitacaoFerias {
  id: string;
  funcionarioId?: string;
  nome: string;
  matricula: string;
  protocolo: string;
  dataInicio: string;
  dataFim: string;
  cargo?: string;
  departamento?: string;
  cidade?: string;
  uf?: string;
  secretariaId?: string;
  secretariaNome?: string;
  periodoAquisitivo?: string;
  dataInicioPortaria?: string;
  dataFimPortaria?: string;
  portariaNumero?: number;
  portariaAno?: number;
  status: StatusSolicitacao;
  parecer?: string;
  solicitadoPor?: string;
  decididoPor?: string;
  criadoEm: string;
  decididoEm?: string;
}

export interface Secretaria {
  id?: string;
  empregadorId: string;
  nome: string;
  ativo: boolean;
}

export interface Secretario {
  id?: string;
  empregadorId: string;
  secretariaId: string;
  secretariaNome?: string;
  nome: string;
}

export interface ResumoSolicitacoes {
  ajustesPendentes: number;
  feriasPendentes: number;
}

export interface Departamento {
  id?: string;
  empregadorId: string;
  nome: string;
  ativo: boolean;
}

export interface EventoAuditoria {
  id: string;
  empregadorId?: string;
  empregador?: string;
  tipo: string;
  origem?: string;
  descricao?: string;
  sucesso: boolean;
  ipOrigem?: string;
  criadoEm: string;
  temCapturaFacial?: boolean;
}

export interface PaginaEventosAuditoria {
  conteudo: EventoAuditoria[];
  pagina: number;
  tamanho: number;
  totalElementos: number;
  totalPaginas: number;
}

export interface PaginaDocumentosOficiais {
  conteudo: DocumentoOficial[];
  pagina: number;
  tamanho: number;
  totalElementos: number;
  totalPaginas: number;
}

export interface PaginaSolicitacoesFerias {
  conteudo: SolicitacaoFerias[];
  pagina: number;
  tamanho: number;
  totalElementos: number;
  totalPaginas: number;
}

export interface Dispositivo {
  id?: string;
  empregador?: Empregador;
  empregadorId?: string;
  nome: string;
  codigoServidor: string;
  tokenTerminal?: string;
  tipo: string;
  localizacao?: string;
  zoomCamera?: number;
  areaLeituraCameraPercentual?: number;
  ipLeitor?: string;
  usuarioLeitor?: string;
  senhaLeitor?: string;
  temSenhaLeitor?: boolean;
  ativo: boolean;
  departamentoIds?: string[];
}

export interface ResultadoImportacaoControlId {
  total: number;
  criados: number;
  atualizados: number;
  comFoto: number;
  semFoto: number;
  ignorados: number;
  avisos: string[];
}

export interface JornadaHorario {
  id?: string;
  sequencia: number;
  tipo: string;
  horario: string;
  toleranciaAntesMinutos: number;
  toleranciaDepoisMinutos: number;
  diasSemana?: string;
  ativo: boolean;
}

export interface JornadaPonto {
  id?: string;
  empregadorId: string;
  nome: string;
  versao?: number;
  padrao: boolean;
  ativa: boolean;
  validarJanelaPonto: boolean;
  toleranciaMinutos: number;
  horarios: JornadaHorario[];
}

export interface LoginResponse {
  token: string;
  usuario: Usuario;
}

export interface TerminalAtivacaoResponse {
  dispositivoId: string;
  nome: string;
  tokenTerminal: string;
  autorizado: boolean;
  dataHoraServidor?: string;
  autorizacaoOfflineExpiraEm?: string;
  validadeOfflineMinutos?: number;
  zoomCamera?: number;
  areaLeituraCameraPercentual?: number;
}

export type ApiError = HttpErrorResponse;

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly baseUrl = this.apiBaseUrl();

  constructor(
    private readonly http: HttpClient,
    private readonly servicoTenant: ServicoTenant
  ) {}

  private apiBaseUrl() {
    if (this.servicoTenant.ehApiLocal()) {
      return 'http://localhost:8080/api';
    }
    return '/api';
  }

  listarEmpregadores() {
    return this.http.get<Empregador[]>(`${this.baseUrl}/empregadores`);
  }

  login(email: string, senha: string, slugTenant?: string) {
    return this.http.post<LoginResponse>(`${this.baseUrl}/auth/login`, {
      email,
      senha,
      slugTenant: slugTenant || this.servicoTenant.slugTenantAtual || undefined
    });
  }

  bootstrap(usuario: Usuario) {
    return this.http.post<LoginResponse>(`${this.baseUrl}/auth/bootstrap`, usuario);
  }

  bootstrapDisponivel() {
    return this.http.get<boolean>(`${this.baseUrl}/auth/bootstrap-disponivel`);
  }

  me() {
    return this.http.get<Usuario>(`${this.baseUrl}/auth/me`);
  }

  salvarEmpregador(empregador: Empregador) {
    if (empregador.id) {
      return this.http.put<Empregador>(`${this.baseUrl}/empregadores/${empregador.id}`, empregador);
    }
    return this.http.post<Empregador>(`${this.baseUrl}/empregadores`, empregador);
  }

  excluirEmpregador(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/empregadores/${id}`);
  }

  listarFuncionarios(empregadorId?: string) {
    let params = new HttpParams();
    if (empregadorId) {
      params = params.set('empregadorId', empregadorId);
    }
    return this.http.get<Funcionario[]>(`${this.baseUrl}/funcionarios`, { params });
  }

  buscarFuncionario(id: string) {
    return this.http.get<Funcionario>(`${this.baseUrl}/funcionarios/${id}`);
  }

  gerarAcessosServidor(empregadorId?: string) {
    let params = new HttpParams();
    if (empregadorId) {
      params = params.set('empregadorId', empregadorId);
    }
    return this.http.post<ResultadoGeracaoAcessos>(`${this.baseUrl}/funcionarios/gerar-acessos`, {}, { params });
  }

  buscarBatidasServidor() {
    return this.http.get<PortalServidorBatidas>(`${this.baseUrl}/servidor/batidas`);
  }

  baixarComprovanteServidor(nsr: number) {
    const params = new HttpParams().set('nsr', String(nsr));
    return this.http.get(`${this.baseUrl}/servidor/comprovante`, {
      params,
      responseType: 'blob'
    });
  }

  alterarSenhaServidor(senhaAtual: string, senhaNova: string) {
    return this.http.post<void>(`${this.baseUrl}/servidor/senha`, { senhaAtual, senhaNova });
  }

  listarAjustesServidor() {
    return this.http.get<SolicitacaoAjustePonto[]>(`${this.baseUrl}/servidor/ajustes`);
  }

  pedirAjusteServidor(data: string, numeroBatida: number, hora: string, motivo: string) {
    return this.http.post<SolicitacaoAjustePonto>(`${this.baseUrl}/servidor/ajustes`, { data, numeroBatida, hora, motivo });
  }

  listarFeriasServidor() {
    return this.http.get<SolicitacaoFerias[]>(`${this.baseUrl}/servidor/ferias`);
  }

  pedirFeriasServidor(dataInicio: string, dataFim: string) {
    return this.http.post<SolicitacaoFerias>(`${this.baseUrl}/servidor/ferias`, { dataInicio, dataFim });
  }

  listarDocumentosServidor() {
    return this.http.get<DocumentoOficial[]>(`${this.baseUrl}/servidor/documentos`);
  }

  pedirDocumentoServidor(tipo: TipoDocumentoOficial, finalidade: string) {
    return this.http.post<DocumentoOficial>(`${this.baseUrl}/servidor/documentos`, { tipo, finalidade });
  }

  listarCadastrosDocumentosOficiais(empregadorId: string) {
    return this.http.get<DocumentoOficial[]>(`${this.baseUrl}/documentos-oficiais/cadastro`, {
      params: new HttpParams().set('empregadorId', empregadorId)
    });
  }

  listarDocumentosOficiais(
    empregadorId: string,
    dataInicial: string,
    dataFinal: string,
    tipo?: TipoDocumentoOficial,
    pagina = 1,
    limite = 50
  ) {
    let params = new HttpParams()
      .set('empregadorId', empregadorId)
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal)
      .set('pagina', pagina)
      .set('limite', limite);
    if (tipo) {
      params = params.set('tipo', tipo);
    }
    return this.http.get<PaginaDocumentosOficiais>(`${this.baseUrl}/documentos-oficiais`, { params });
  }

  cadastrarDocumentoOficial(empregadorId: string, tipo: TipoDocumentoOficial, numero: number, ano: number) {
    return this.http.post<DocumentoOficial>(`${this.baseUrl}/documentos-oficiais`, { tipo, numero, ano }, {
      params: new HttpParams().set('empregadorId', empregadorId)
    });
  }

  reservarDocumentosOficiais(empregadorId: string, tipo: TipoDocumentoOficial, ano: number, quantidade: number) {
    return this.http.post<DocumentoOficial[]>(`${this.baseUrl}/documentos-oficiais/reservas`, {
      tipo,
      ano,
      quantidade
    }, {
      params: new HttpParams().set('empregadorId', empregadorId)
    });
  }

  editarDocumentoOficial(
    id: string,
    tipo: TipoDocumentoOficial,
    numero: number,
    ano: number,
    dataSolicitacao: string,
    finalidade?: string
  ) {
    return this.http.put<DocumentoOficial>(`${this.baseUrl}/documentos-oficiais/${id}`, {
      tipo,
      numero,
      ano,
      dataSolicitacao,
      finalidade: finalidade || ''
    });
  }

  removerSolicitanteDocumentoOficial(id: string) {
    return this.http.post<DocumentoOficial>(`${this.baseUrl}/documentos-oficiais/${id}/remover-solicitante`, {});
  }

  resumoSolicitacoes(empregadorId: string) {
    return this.http.get<ResumoSolicitacoes>(`${this.baseUrl}/solicitacoes/resumo`, {
      params: new HttpParams().set('empregadorId', empregadorId)
    });
  }

  listarAjustesGerencial(empregadorId: string, status?: StatusSolicitacao) {
    let params = new HttpParams().set('empregadorId', empregadorId);
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<SolicitacaoAjustePonto[]>(`${this.baseUrl}/solicitacoes/ajustes`, { params });
  }

  decidirAjusteGerencial(id: string, deferida: boolean, parecer?: string) {
    return this.http.post<SolicitacaoAjustePonto>(`${this.baseUrl}/solicitacoes/ajustes/${id}/decidir`, { deferida, parecer });
  }

  listarFeriasGerencial(empregadorId: string, status?: StatusSolicitacao) {
    let params = new HttpParams().set('empregadorId', empregadorId);
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<SolicitacaoFerias[]>(`${this.baseUrl}/solicitacoes/ferias`, { params });
  }

  listarFeriasListagem(
    empregadorId: string,
    dataInicial: string,
    dataFinal: string,
    status?: StatusSolicitacao,
    pagina = 1,
    limite = 50
  ) {
    let params = new HttpParams()
      .set('empregadorId', empregadorId)
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal)
      .set('pagina', pagina)
      .set('limite', limite);
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<PaginaSolicitacoesFerias>(`${this.baseUrl}/solicitacoes/ferias/listagem`, { params });
  }

  editarFeriasGerencial(
    id: string,
    payload: {
      dataInicio: string;
      dataFim: string;
      periodoAquisitivo?: string;
      dataInicioPortaria?: string;
      dataFimPortaria?: string;
    }
  ) {
    return this.http.put<SolicitacaoFerias>(`${this.baseUrl}/solicitacoes/ferias/${id}`, payload);
  }

  pedirFeriasGerencial(funcionarioId: string, dataInicio: string, dataFim: string) {
    return this.http.post<SolicitacaoFerias>(`${this.baseUrl}/solicitacoes/ferias`, {
      funcionarioId,
      dataInicio,
      dataFim
    });
  }

  decidirFeriasGerencial(
    id: string,
    deferida: boolean,
    parecer?: string,
    extra?: { secretariaId?: string; periodoAquisitivo?: string; dataInicio?: string; dataFim?: string }
  ) {
    return this.http.post<SolicitacaoFerias>(`${this.baseUrl}/solicitacoes/ferias/${id}/decidir`, {
      deferida,
      parecer,
      ...extra
    });
  }

  baixarPortariaFerias(id: string) {
    return this.http.get(`${this.baseUrl}/solicitacoes/ferias/${id}/portaria`, { responseType: 'blob' });
  }

  listarSecretarias(empregadorId: string, incluirInativos = false) {
    let params = new HttpParams().set('empregadorId', empregadorId);
    if (incluirInativos) {
      params = params.set('incluirInativos', 'true');
    }
    return this.http.get<Secretaria[]>(`${this.baseUrl}/secretarias`, { params });
  }

  salvarSecretaria(secretaria: Secretaria) {
    if (secretaria.id) {
      return this.http.put<Secretaria>(`${this.baseUrl}/secretarias/${secretaria.id}`, secretaria);
    }
    return this.http.post<Secretaria>(`${this.baseUrl}/secretarias`, secretaria);
  }

  excluirSecretaria(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/secretarias/${id}`);
  }

  listarSecretarios(empregadorId: string) {
    return this.http.get<Secretario[]>(`${this.baseUrl}/secretarios`, {
      params: new HttpParams().set('empregadorId', empregadorId)
    });
  }

  salvarSecretario(secretario: Secretario) {
    if (secretario.id) {
      return this.http.put<Secretario>(`${this.baseUrl}/secretarios/${secretario.id}`, secretario);
    }
    return this.http.post<Secretario>(`${this.baseUrl}/secretarios`, secretario);
  }

  excluirSecretario(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/secretarios/${id}`);
  }

  salvarFuncionario(funcionario: Funcionario) {
    if (funcionario.id) {
      return this.http.put<Funcionario>(`${this.baseUrl}/funcionarios/${funcionario.id}`, funcionario);
    }
    return this.http.post<Funcionario>(`${this.baseUrl}/funcionarios`, funcionario);
  }

  validarFaceFuncionario(funcionarioId: string, descritorFacial: number[]) {
    return this.http.post<void>(`${this.baseUrl}/funcionarios/${funcionarioId}/validar-face`, { descritorFacial });
  }

  cadastrarFaceFuncionario(funcionarioId: string, imagemFacialBase64: string, imagemFacialMimeType: string, descritorFacial: number[]) {
    return this.http.post<Funcionario>(`${this.baseUrl}/funcionarios/${funcionarioId}/face`, {
      imagemFacialBase64,
      imagemFacialMimeType,
      descritorFacial
    });
  }

  removerFaceFuncionario(funcionarioId: string, faceId: string) {
    return this.http.delete<Funcionario>(`${this.baseUrl}/funcionarios/${funcionarioId}/faces/${faceId}`);
  }

  excluirFuncionario(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/funcionarios/${id}`);
  }

  listarJornadas(empregadorId: string) {
    const params = new HttpParams().set('empregadorId', empregadorId);
    return this.http.get<JornadaPonto[]>(`${this.baseUrl}/jornadas-ponto`, { params });
  }

  listarDepartamentos(empregadorId: string, incluirInativos = false) {
    let params = new HttpParams().set('empregadorId', empregadorId);
    if (incluirInativos) {
      params = params.set('incluirInativos', 'true');
    }
    return this.http.get<Departamento[]>(`${this.baseUrl}/departamentos`, { params });
  }

  salvarDepartamento(departamento: Departamento) {
    if (departamento.id) {
      return this.http.put<Departamento>(`${this.baseUrl}/departamentos/${departamento.id}`, departamento);
    }
    return this.http.post<Departamento>(`${this.baseUrl}/departamentos`, departamento);
  }

  excluirDepartamento(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/departamentos/${id}`);
  }

  salvarJornada(jornada: JornadaPonto) {
    if (jornada.id) {
      return this.http.put<JornadaPonto>(`${this.baseUrl}/jornadas-ponto/${jornada.id}`, jornada);
    }
    return this.http.post<JornadaPonto>(`${this.baseUrl}/jornadas-ponto`, jornada);
  }

  excluirJornada(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/jornadas-ponto/${id}`);
  }

  listarUsuarios(empregadorId?: string) {
    let params = new HttpParams();
    if (empregadorId) {
      params = params.set('empregadorId', empregadorId);
    }
    return this.http.get<Usuario[]>(`${this.baseUrl}/usuarios`, { params });
  }

  salvarUsuario(usuario: Usuario) {
    if (usuario.id) {
      return this.http.put<Usuario>(`${this.baseUrl}/usuarios/${usuario.id}`, usuario);
    }
    return this.http.post<Usuario>(`${this.baseUrl}/usuarios`, usuario);
  }

  excluirUsuario(id: string) {
    return this.http.delete<void>(`${this.baseUrl}/usuarios/${id}`);
  }

  listarEventosAuditoria(
    empregadorId?: string,
    tipo?: string,
    limite = 50,
    dataInicial?: string,
    dataFinal?: string,
    nomeServidor?: string,
    pagina = 1,
    sucesso?: boolean
  ) {
    let params = new HttpParams()
      .set('limite', limite)
      .set('pagina', pagina);
    if (empregadorId) {
      params = params.set('empregadorId', empregadorId);
    }
    if (tipo) {
      params = params.set('tipo', tipo);
    }
    if (dataInicial) {
      params = params.set('dataInicial', dataInicial);
    }
    if (dataFinal) {
      params = params.set('dataFinal', dataFinal);
    }
    if (nomeServidor) {
      params = params.set('nomeServidor', nomeServidor);
    }
    if (sucesso === true || sucesso === false) {
      params = params.set('sucesso', String(sucesso));
    }
    return this.http.get<PaginaEventosAuditoria>(`${this.baseUrl}/auditoria/eventos`, { params });
  }

  listarDispositivos(empregadorId?: string) {
    let params = new HttpParams();
    if (empregadorId) {
      params = params.set('empregadorId', empregadorId);
    }
    return this.http.get<Dispositivo[]>(`${this.baseUrl}/dispositivos`, { params });
  }

  salvarDispositivo(dispositivo: Dispositivo) {
    if (dispositivo.id) {
      return this.http.put<Dispositivo>(`${this.baseUrl}/dispositivos/${dispositivo.id}`, dispositivo);
    }
    return this.http.post<Dispositivo>(`${this.baseUrl}/dispositivos`, dispositivo);
  }

  excluirDispositivo(dispositivoId: string) {
    return this.http.delete<void>(`${this.baseUrl}/dispositivos/${dispositivoId}`);
  }

  revogarTokenDispositivo(dispositivoId: string) {
    return this.http.post<Dispositivo>(`${this.baseUrl}/dispositivos/${dispositivoId}/revogar-token`, {});
  }

  importarServidoresControlId(dispositivoId: string, payload: { ipLeitor?: string; usuarioLeitor?: string; senhaLeitor?: string }) {
    return this.http.post<ResultadoImportacaoControlId>(`${this.baseUrl}/dispositivos/${dispositivoId}/importar-controlid`, payload);
  }

  habilitarFotoMonitorControlId(dispositivoId: string, payload: { ipLeitor?: string; usuarioLeitor?: string; senhaLeitor?: string }) {
    return this.http.post<void>(`${this.baseUrl}/dispositivos/${dispositivoId}/foto-monitor-controlid`, payload);
  }

  registrarPonto(funcionarioId: string, imagemFacialBase64?: string, descritorFacial?: number[], offlineId?: string) {
    return this.http.post<RegistroPonto>(`${this.baseUrl}/registros-ponto`, {
      funcionarioId,
      metodo: imagemFacialBase64 ? 'FACIAL' : 'PROTOTIPO',
      observacao: imagemFacialBase64
        ? 'Registro facial feito pelo painel web prototipo'
        : 'Registro feito pelo painel web prototipo',
      imagemFacialBase64,
      imagemFacialMimeType: imagemFacialBase64 ? 'image/jpeg' : undefined,
      descritorFacial,
      offlineId
    });
  }

  registrarPontoPorFace(empregadorId: string, imagemFacialBase64: string, descritorFacial: number[], offlineId?: string) {
    return this.http.post<RegistroPonto>(`${this.baseUrl}/registros-ponto/facial`, {
      empregadorId,
      imagemFacialBase64,
      imagemFacialMimeType: 'image/jpeg',
      descritorFacial,
      offlineId
    });
  }

  buscarTenantPublico() {
    const slug = this.servicoTenant.slugTenantAtual;
    if (slug) {
      return this.http.get<Empregador>(`${this.baseUrl}/public/tenants/${slug}`);
    }
    return this.http.get<Empregador>(`${this.baseUrl}/public/tenant`);
  }

  ativarTerminalPublico(codigoServidor: string) {
    const slug = this.servicoTenant.slugTenantAtual;
    return this.http.post<TerminalAtivacaoResponse>(`${this.baseUrl}/public/tenants/${slug}/terminais/ativar`, { codigoServidor });
  }

  validarTerminalPublico(token: string) {
    const slug = this.servicoTenant.slugTenantAtual;
    const params = new HttpParams().set('token', token);
    return this.http.get<TerminalAtivacaoResponse>(`${this.baseUrl}/public/tenants/${slug}/terminais/validar`, { params });
  }

  registrarPontoPublicoPorFace(imagemFacialBase64: string, descritorFacial: number[], terminalToken: string, offlineId?: string) {
    const slug = this.servicoTenant.slugTenantAtual;
    const url = slug
      ? `${this.baseUrl}/public/tenants/${slug}/registros-ponto/facial`
      : `${this.baseUrl}/public/registros-ponto/facial`;

    return this.http.post<RegistroPonto>(url, {
      empregadorId: '00000000-0000-0000-0000-000000000000',
      terminalToken,
      imagemFacialBase64,
      imagemFacialMimeType: 'image/jpeg',
      descritorFacial,
      offlineId
    });
  }

  sincronizarRegistrosOffline(terminalToken: string, registros: RegistroPontoOfflineEnvio[]) {
    const slug = this.servicoTenant.slugTenantAtual;
    const url = slug
      ? `${this.baseUrl}/public/tenants/${slug}/registros-ponto/offline/sincronizar`
      : `${this.baseUrl}/public/registros-ponto/offline/sincronizar`;
    return this.http.post<SincronizacaoOfflineResponse>(
      url,
      {
        terminalToken,
        registros: registros.map(registro => ({
          empregadorId: '00000000-0000-0000-0000-000000000000',
          imagemFacialBase64: registro.imagemFacialBase64,
          imagemFacialMimeType: registro.imagemFacialMimeType,
          descritorFacial: registro.descritorFacial,
          offlineId: registro.offlineId,
          dataHora: registro.dataHora
        }))
      }
    );
  }

  buscarCacheBiometricoTerminal(terminalToken: string) {
    const slug = this.servicoTenant.slugTenantAtual;
    return this.http.post<CacheBiometricoResponse>(
      `${this.baseUrl}/public/tenants/${slug}/biometria/cache`,
      { terminalToken }
    );
  }

  listarRegistros(empregadorId: string, dataInicial: string, dataFinal: string, funcionarioId?: string, departamentoIds?: string[]) {
    const params = this.aplicarDepartamentos(
      this.paramsPeriodo(empregadorId, dataInicial, dataFinal, funcionarioId),
      departamentoIds
    );
    return this.http.get<RegistroPonto[]>(`${this.baseUrl}/registros-ponto`, { params });
  }

  buscarCapturaPonto(empregadorId: string, nsr: number) {
    const params = new HttpParams()
      .set('empregadorId', empregadorId)
      .set('nsr', String(nsr));
    return this.http.get(`${this.baseUrl}/registros-ponto/captura`, {
      params,
      responseType: 'blob'
    });
  }

  buscarCapturaFalhaPonto(empregadorId: string, eventoId: string) {
    const params = new HttpParams()
      .set('empregadorId', empregadorId)
      .set('eventoId', eventoId);
    return this.http.get(`${this.baseUrl}/registros-ponto/captura-falha`, {
      params,
      responseType: 'blob'
    });
  }

  previewAcertoPonto(empregadorId: string, dataInicial: string, dataFinal: string, funcionarioId?: string, departamentoIds?: string[]) {
    const params = this.aplicarDepartamentos(
      this.paramsPeriodo(empregadorId, dataInicial, dataFinal, funcionarioId),
      departamentoIds
    );
    return this.http.get<PreviewAcertoPonto>(`${this.baseUrl}/registros-ponto/acerto/preview`, { params });
  }

  aplicarAcertoPonto(
    empregadorId: string,
    dataInicial: string,
    dataFinal: string,
    eventoIds: string[],
    funcionarioId?: string,
    departamentoIds?: string[]
  ) {
    const ids = (departamentoIds || []).filter(Boolean);
    return this.http.post<ResultadoAcertoPonto>(`${this.baseUrl}/registros-ponto/acerto`, {
      empregadorId,
      dataInicial,
      dataFinal,
      funcionarioId,
      departamentoId: ids.length === 1 ? ids[0] : undefined,
      departamentoIds: ids.length > 1 ? ids : undefined,
      eventoIds
    });
  }

  ajustarFolhaDia(funcionarioId: string, data: string, batidas: string[]) {
    return this.http.put<RegistroPonto[]>(`${this.baseUrl}/registros-ponto/folha-dia`, {
      funcionarioId,
      data,
      batidas
    });
  }

  listarAlteracoesFolha(empregadorId: string, funcionarioId: string, data: string) {
    const params = new HttpParams()
      .set('empregadorId', empregadorId)
      .set('funcionarioId', funcionarioId)
      .set('data', data);
    return this.http.get<RegistroPontoAlteracao[]>(`${this.baseUrl}/registros-ponto/alteracoes`, { params });
  }

  baixarExportacao(empregadorId: string, dataInicial: string, dataFinal: string, formato: 'csv' | 'txt', funcionarioId?: string, departamentoIds?: string[]) {
    const params = this.aplicarDepartamentos(
      this.paramsPeriodo(empregadorId, dataInicial, dataFinal, funcionarioId).set('formato', formato),
      departamentoIds
    );
    return this.http.get(`${this.baseUrl}/exportacoes/ponto`, {
      params,
      responseType: 'blob'
    });
  }

  baixarFolhaPontoPdf(empregadorId: string, funcionarioId: string, dataInicial: string, dataFinal: string) {
    const params = new HttpParams()
      .set('empregadorId', empregadorId)
      .set('funcionarioId', funcionarioId)
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal);
    return this.http.get(`${this.baseUrl}/relatorios/folha-ponto.pdf`, {
      params,
      responseType: 'blob'
    });
  }

  baixarFolhasPontoPdf(empregadorId: string, dataInicial: string, dataFinal: string, funcionarioId?: string, departamentoIds?: string[]) {
    const params = this.aplicarDepartamentos(
      this.paramsPeriodo(empregadorId, dataInicial, dataFinal, funcionarioId),
      departamentoIds
    );
    return this.http.get(`${this.baseUrl}/relatorios/folhas-ponto.pdf`, {
      params,
      responseType: 'blob'
    });
  }

  baixarPendenciasPontoPdf(empregadorId: string, dataInicial: string, dataFinal: string, funcionarioId?: string, departamentoIds?: string[]) {
    const params = this.aplicarDepartamentos(
      this.paramsPeriodo(empregadorId, dataInicial, dataFinal, funcionarioId),
      departamentoIds
    );
    return this.http.get(`${this.baseUrl}/relatorios/pendencias-ponto.pdf`, {
      params,
      responseType: 'blob'
    });
  }

  listarPendenciasPonto(empregadorId: string, dataInicial: string, dataFinal: string, funcionarioId?: string, departamentoIds?: string[]) {
    const params = this.aplicarDepartamentos(
      this.paramsPeriodo(empregadorId, dataInicial, dataFinal, funcionarioId),
      departamentoIds
    );
    return this.http.get<PendenciaPontoLinha[]>(`${this.baseUrl}/pendencias-ponto`, { params });
  }

  private paramsPeriodo(empregadorId: string, dataInicial: string, dataFinal: string, funcionarioId?: string) {
    let params = new HttpParams()
      .set('empregadorId', empregadorId)
      .set('dataInicial', dataInicial)
      .set('dataFinal', dataFinal);
    if (funcionarioId) {
      params = params.set('funcionarioId', funcionarioId);
    }
    return params;
  }

  private aplicarDepartamentos(params: HttpParams, departamentoIds?: string[]) {
    const ids = (departamentoIds || []).filter(Boolean);
    if (ids.length === 1) {
      return params.set('departamentoId', ids[0]);
    }
    for (const id of ids) {
      params = params.append('departamentoIds', id);
    }
    return params;
  }
}
